import { useEffect, useState } from 'react';
import { FiUser } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Button from '~/components/button';
import Container from '~/components/container';
import Loading from '~/components/loading';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import { creditCardMask, currencyMask } from '~/utils/masks';
import { ActiveAgreement } from '..';

import * as St from '../../Client/styles';
import * as Tl from './styles';

interface DebtItem {
  codigo?: string;
  dataVencimento?: string;
  id: string;
  parcelaAtual?: number;
  totalParcelas?: number;
  valorComDesconto?: number;
  valorPendente?: number;
  vencimento?: string;
}

interface DebtGroup {
  label: string;
  notice: string;
  items: DebtItem[];
}

interface AgreementClient {
  cartaoTeucard?: {
    limiteCreditoUtilizado?: number;
    limiteEmprestimoUtilizado?: number;
    limiteExtraUtilizado?: number;
    limiteTotalUtilizado?: number;
    numeroCartao?: string;
    status?: string;
  };
  compras?: DebtItem[];
  comprasLimite12x?: DebtItem[];
  cpf?: string;
  emprestimos?: DebtItem[];
  financiamentos?: DebtItem[];
  fotoCliente?: string;
  nome?: string;
  statusFatura?: string;
  valorTotalTransacoes?: number;
  valorUltimaFatura?: number;
  vencimentoFatura?: string;
}

interface PaymentOption {
  parcelas?: number;
  valorComDesconto?: number;
  valorDesconto?: number;
  valorOriginal?: number;
  valorParcela?: number;
  vencimento?: string;
}

interface Negotiation {
  modalidade?: 'A_VISTA' | 'PARCELADO';
  nomeOperador?: string;
  opcaoPagamento?: PaymentOption;
  percentualDesconto?: number;
  vencimentoPrimeiroPagamento?: string;
}

const HISTORY = [
  {
    tone: 'gray' as const,
    date: '12/05/2026 às 14:10',
    title: 'Simulação gerada',
    description:
      'Operador Maria Silva gerou simulação de acordo com tabela padrão.',
  },
  {
    tone: 'red' as const,
    date: '12/05/2026 às 14:32',
    title: 'Acordo confirmado',
    description: 'Acordo aceito e registrado pelo cliente',
  },
  {
    tone: 'green' as const,
    date: '14/05/2026 às 09:15',
    title: 'Entrada identificada e acordo ativo',
    description:
      'Pagamento da entrada compensado via Pix bancário e acordo efetivado.',
  },
];

const DEBT_COLLECTIONS: Record<string, Pick<DebtGroup, 'label' | 'notice'>> = {
  financiamentos: {
    label: 'Financiamento de faturas ativos',
    notice:
      'O financiamento de faturas será considerado no cálculo do acordo.',
  },
  emprestimos: {
    label: 'Financiamento de empréstimos ativos',
    notice:
      'O financiamento de empréstimo será considerado no cálculo do acordo.',
  },
  compras: {
    label: 'Financiamento de compras ativos',
    notice:
      'O financiamento de compras será considerado no cálculo do acordo.',
  },
  comprasLimite12x: {
    label: 'Financiamento de limite 12x ativos',
    notice:
      'O financiamento de limite 12x será considerado no cálculo do acordo.',
  },
};

const formatMoney = (value?: number) =>
  value === undefined || value === null ? '-' : `R$ ${currencyMask(value)}`;

const formatDate = (value?: string) =>
  value ? moment(value).format('DD/MM/YYYY') : '-';

const asDebtItems = (items?: unknown) =>
  Array.isArray(items) ? (items as DebtItem[]) : [];

const readPayload = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  const source = record.consulta || record.negociacao ? record : nested;

  if (!source) return null;

  const consulta = source.consulta as { dadosConsulta?: AgreementClient } | undefined;
  const dadosConsulta =
    consulta?.dadosConsulta ||
    (source.cartaoTeucard || source.nome ? (source as AgreementClient) : undefined);

  return {
    client: dadosConsulta || null,
    negotiation: (source.negociacao as Negotiation) || null,
  };
};

const DebtNegotiationActiveAgreementDetail = (): JSX.Element => {
  const history = useHistory<ActiveAgreement>();
  const agreement = history.location.state;
  const { user } = useAuth();
  const [client, setClient] = useState<AgreementClient | null>(null);
  const [negotiation, setNegotiation] = useState<Negotiation | null>(null);
  const [feedback, setFeedback] = useState('');
  const getNegotiationDetail = useApi(
    Endpoints.debtNegotiation.detailNegotiation,
    ApiMethod.POST,
  );

  useEffect(() => {
    const loadDetail = async () => {
      const idNegociacao = agreement?.idNegociacao;
      const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

      if (!idNegociacao || !cpfOperador) {
        if (!idNegociacao) setFeedback('Negociação não informada.');
        return;
      }

      try {
        const response = await getNegotiationDetail.callApi({
          idNegociacao,
          cpfOperador,
        });

        if (!(response.status >= 200 && response.status < 300)) {
          setFeedback(
            response.mensagem || 'Não foi possível consultar o acordo.',
          );
          return;
        }

        const payload = readPayload(response.data);

        if (!payload?.client && !payload?.negotiation) {
          setFeedback('A consulta não retornou os dados do acordo.');
          return;
        }

        setFeedback('');
        setClient(payload?.client || null);
        setNegotiation(payload?.negotiation || null);
      } catch (error) {
        setFeedback(
          error instanceof Error
            ? error.message
            : 'Não foi possível consultar o acordo.',
        );
      }
    };

    loadDetail();
  }, [agreement?.idNegociacao, user?.cpf]);

  const option = negotiation?.opcaoPagamento;
  const negotiationType =
    negotiation?.modalidade === 'PARCELADO' ? 'parcelado' : 'avista';
  const discount = negotiation?.percentualDesconto;
  const dueDateValue = negotiation?.vencimentoPrimeiroPagamento || option?.vencimento;
  const entryValue = option?.valorParcela;
  const installmentValue = option?.valorParcela;
  const appliedDiscount = option?.valorDesconto;
  const debtTotal = option?.valorOriginal;
  const installments = option?.parcelas;
  const currentInvoice = client?.valorUltimaFatura;
  const futureInvoices =
    client?.valorTotalTransacoes != null && currentInvoice != null
      ? Number((client.valorTotalTransacoes - currentInvoice).toFixed(2))
      : undefined;
  const debtSummaryTotal =
    currentInvoice != null && futureInvoices != null
      ? Number((currentInvoice + futureInvoices).toFixed(2))
      : undefined;
  const card = client?.cartaoTeucard;
  const debtGroups: DebtGroup[] = client
    ? Object.entries(client).flatMap(([key, value]) => {
        const collection = DEBT_COLLECTIONS[key];
        const items = asDebtItems(value);

        if (!collection || items.length === 0) return [];

        return [{ ...collection, items }];
      })
    : [];
  const cardNumber = card?.numeroCartao;

  const fields = [
    { label: 'Nome', value: client?.nome || agreement?.nome || '-' },
    {
      label: 'Cartão',
      value: cardNumber ? creditCardMask(cardNumber).masked : '-',
    },
    { label: 'Status do cartão', value: card?.status || '-' },
    {
      label: 'Limite total utilizado',
      value: formatMoney(card?.limiteTotalUtilizado),
    },
    {
      label: 'Limite de compras',
      value: formatMoney(card?.limiteCreditoUtilizado),
    },
    {
      label: 'Limite de empréstimo',
      value: formatMoney(card?.limiteEmprestimoUtilizado),
    },
    {
      label: 'Limite extra 12x',
      value: formatMoney(card?.limiteExtraUtilizado),
    },
    { label: 'Limite total', value: formatMoney(card?.limiteTotalUtilizado) },
  ];

  return (
    <Container>
      <Loading status={getNegotiationDetail.loading} />
      {feedback && <St.Feedback>{feedback}</St.Feedback>}
      <St.BoxCard>
        <St.Summary>
          <St.Avatar>
            {client?.fotoCliente ? (
              <img src={client.fotoCliente} alt={client.nome || 'Cliente'} />
            ) : (
              <FiUser />
            )}
          </St.Avatar>
          <St.Fields>
            {fields.map((field) => (
              <St.Field key={field.label}>
                <St.Label>{field.label}</St.Label>
                <St.Value>{field.value}</St.Value>
              </St.Field>
            ))}
          </St.Fields>
        </St.Summary>
      </St.BoxCard>

      <St.Boxes>
        <St.SideColumn>
          <St.Panel>
            <St.PanelTitle>Resumo financeiro</St.PanelTitle>
            <St.FinanceSections>
              <St.FinanceSection>
                <St.SectionTitle>Última fatura (Atual)</St.SectionTitle>
                <St.PlainText>
                  Vencimento 
                  {' '}
                  {formatDate(client?.vencimentoFatura)}
                </St.PlainText>
                <St.PlainText>Valor total</St.PlainText>
                <St.Amount>{formatMoney(currentInvoice)}</St.Amount>
                <St.Status>
                  <St.StatusDot />
                  {client?.statusFatura || '-'}
                </St.Status>
              </St.FinanceSection>
              <St.FinanceSection>
                <St.SectionTitle>Faturas futuras</St.SectionTitle>
                <St.PlainText>Próximas faturas</St.PlainText>
                <St.PlainText>Valor total</St.PlainText>
                <St.Amount>{formatMoney(futureInvoices)}</St.Amount>
              </St.FinanceSection>
              <St.FinanceSection>
                <St.SectionTitle>Total da dívida</St.SectionTitle>
                <St.PlainText>Atual + futuras</St.PlainText>
                <St.PlainText>Valor total</St.PlainText>
                <St.Amount>{formatMoney(debtSummaryTotal)}</St.Amount>
                <St.SectionNote>
                  {formatMoney(currentInvoice)}
                  {' '}
                  +
                  {formatMoney(futureInvoices)}
                </St.SectionNote>
              </St.FinanceSection>
            </St.FinanceSections>
          </St.Panel>
          {debtGroups.length === 0 ? (
            <St.Panel $compact>
              <St.MutedText>Não possui dívidas.</St.MutedText>
            </St.Panel>
          ) : (
            <St.DebtColumn>
              {debtGroups.map((group) => (
                <St.DebtPanel key={group.label} $compact>
                  <St.DebtType>{group.label}</St.DebtType>
                  {group.items.map((item) => (
                    <St.DebtCard key={item.id}>
                      <St.DebtFields>
                        <St.FinanceSection>
                          <St.MutedText>Número do financiamento</St.MutedText>
                          <St.PlainText>{item.codigo || '-'}</St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>Saldo devedor</St.MutedText>
                          <St.PlainText>
                            {formatMoney(item.valorPendente)}
                          </St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>
                            Saldo devedor após antecipação
                          </St.MutedText>
                          <St.PlainText>
                            {formatMoney(item.valorComDesconto)}
                          </St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>Parcelas</St.MutedText>
                          <St.PlainText>
                            {item.parcelaAtual ?? '-'}
                            /
                            {item.totalParcelas ?? '-'}
                          </St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>Vencimento</St.MutedText>
                          <St.PlainText>
                            {formatDate(item.dataVencimento || item.vencimento)}
                          </St.PlainText>
                        </St.FinanceSection>
                      </St.DebtFields>
                    </St.DebtCard>
                  ))}
                  <St.DebtNoticeRow>
                    <St.InfoNotice>
                      <St.InfoBadge>i</St.InfoBadge>
                      <span>{group.notice}</span>
                    </St.InfoNotice>
                    <St.FooterButton>
                      <Button outlined fitContent>
                        <span>Ver detalhes</span>
                      </Button>
                    </St.FooterButton>
                  </St.DebtNoticeRow>
                </St.DebtPanel>
              ))}
            </St.DebtColumn>
          )}
        </St.SideColumn>
        <St.SideColumn>
          <St.Panel>
            <St.ConfigStack>
              <div>
                <St.PanelTitle>Configurar negociação</St.PanelTitle>
                <St.ConfigBlock>
                  <St.SummaryTabs>
                    <St.SummaryTab
                      as="div"
                      $active={negotiationType === 'avista'}
                    >
                      À vista
                    </St.SummaryTab>
                    <St.SummaryTab
                      as="div"
                      $active={negotiationType === 'parcelado'}
                    >
                      Parcelado
                    </St.SummaryTab>
                  </St.SummaryTabs>
                  <St.EntryRow>
                    <St.EntryColumn>
                      <St.EntryLabel>Valor da entrada</St.EntryLabel>
                      <St.Amount>{formatMoney(entryValue)}</St.Amount>
                    </St.EntryColumn>
                    <St.EntryColumn $end>
                      <St.EntryLabel>Entrada (%)</St.EntryLabel>
                      <St.PercentGroup>
                        <St.PercentField
                          value={discount === undefined ? '' : String(discount)}
                          readOnly
                        />
                        <St.PercentSuffix>%</St.PercentSuffix>
                      </St.PercentGroup>
                    </St.EntryColumn>
                  </St.EntryRow>
                  {negotiationType === 'avista' ? (
                    <St.InfoNotice>
                      <St.InfoBadge>i</St.InfoBadge>
                      <span>
                        A nova fatura será gerada com o valor da entrada para
                        negociação à vista.
                      </span>
                    </St.InfoNotice>
                  ) : (
                    <>
                      <St.InfoNotice>
                        <St.InfoBadge>i</St.InfoBadge>
                        <span>
                          Após o pagamento da entrada, as parcelas serão
                          lançadas mensalmente.
                        </span>
                      </St.InfoNotice>
                      <St.InstallmentRow>
                        <St.EntryColumn>
                          <St.EntryLabel>Valor da entrada</St.EntryLabel>
                          <St.Amount>{formatMoney(entryValue)}</St.Amount>
                        </St.EntryColumn>
                        <St.EntryColumn>
                          <St.EntryLabel>Número de parcelas</St.EntryLabel>
                          <St.Amount>
                            {installments
                              ? `${installments} ${
                                  installments === 1 ? 'parcela' : 'parcelas'
                                }`
                              : '-'}
                          </St.Amount>
                        </St.EntryColumn>
                        <St.EntryColumn>
                          <St.EntryLabel>Valor das parcelas</St.EntryLabel>
                          <St.Amount>{formatMoney(installmentValue)}</St.Amount>
                          <St.MutedText>Taxa de juros: 5,9% ao mês</St.MutedText>
                        </St.EntryColumn>
                      </St.InstallmentRow>
                    </>
                  )}
                  <St.NewInvoice>
                    <St.PanelTitle>Nova fatura (Após acordo)</St.PanelTitle>
                    <St.EntryColumn>
                      <St.PlainText>Vencimento</St.PlainText>
                      <St.Amount>{formatDate(dueDateValue)}</St.Amount>
                    </St.EntryColumn>
                    <St.NewInvoiceValue>
                      <St.PlainText>Valor da nova fatura</St.PlainText>
                      <St.DiscountAmount>
                        {formatMoney(entryValue)}
                      </St.DiscountAmount>
                    </St.NewInvoiceValue>
                  </St.NewInvoice>
                  <div>
                    <St.PanelTitle>Resumo da negociação</St.PanelTitle>
                    <St.ConfigBlock>
                      <St.SummaryLine>
                        <St.EntryLabel>Desconto aplicado</St.EntryLabel>
                        <St.DiscountAmount>
                          - 
                          {' '}
                          {formatMoney(appliedDiscount)}
                        </St.DiscountAmount>
                      </St.SummaryLine>
                      <St.SummaryLine>
                        <St.EntryLabel>Total da dívida</St.EntryLabel>
                        <St.PlainText>{formatMoney(debtTotal)}</St.PlainText>
                      </St.SummaryLine>
                      <St.WarningNotice>
                        <St.WarningBadge>
                          <span>i</span>
                        </St.WarningBadge>
                        <span>
                          O acordo será representado por uma nova fatura que
                          será gerada com o vencimento em até 15 dias
                        </span>
                      </St.WarningNotice>
                    </St.ConfigBlock>
                  </div>
                </St.ConfigBlock>
              </div>
            </St.ConfigStack>
          </St.Panel>
          <St.Panel>
            <St.PanelTitle>Histórico negociação</St.PanelTitle>
            <Tl.Timeline>
              {HISTORY.map((event, index) => (
                <Tl.Event key={event.title}>
                  <Tl.Rail>
                    <Tl.Dot $tone={event.tone} />
                    {index < HISTORY.length - 1 && <Tl.Line />}
                  </Tl.Rail>
                  <Tl.EventBody>
                    <Tl.EventDate>{event.date}</Tl.EventDate>
                    <Tl.EventTitle $tone={event.tone}>{event.title}</Tl.EventTitle>
                    <Tl.EventDescription>{event.description}</Tl.EventDescription>
                  </Tl.EventBody>
                </Tl.Event>
              ))}
            </Tl.Timeline>
            <Tl.Actions>
              <Button
                fullWidth
                type="button"
                onClick={() => history.push('/negociacao-dividas/buscar-cliente')}
              >
                <span>Simular nova negociação</span>
              </Button>
              <Button
                fullWidth
                outlined
                type="button"
                onClick={() =>
                  history.push(
                    history.location.pathname.includes('historico')
                      ? '/negociacao-dividas/historico'
                      : '/negociacao-dividas/acordos-ativos',
                  )
                }
              >
                <span>Voltar</span>
              </Button>
            </Tl.Actions>
          </St.Panel>
        </St.SideColumn>
      </St.Boxes>
    </Container>
  );
};

export default DebtNegotiationActiveAgreementDetail;
